import React, { useEffect, useState } from 'react';

import { isEqual } from 'lodash';
import { unparse } from 'papaparse';
import fileDownload from 'react-file-download';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import { Link as RouterLink } from '~/lib/react-router-dom';
import dayjs from '~/timeInit';

import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  GridLegacy as Grid,
  Link,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import LereService from '~/services/LereService';
import {
  dateNowUtc,
  formatDateNumeralMonths,
  formatDateSlashes,
  formatDateSlashesWithClock,
} from '~/services/TimeService';

import currencyFormatter from '~/utils/currencyFormatter';

import GridPagination from '../Common/Elements/GridPagination';
import { Checkbox as FormCheckbox, Select } from '../Common/Forms/fields';
import Input from '../Common/Forms/fields/Input';

import CharterTypeFiltersDropdown from './CharterTypeFiltersDropdown';
import ConfidenceScoreFormatter from './formatters/ConfidenceScoreFormatter';
import NameAndIdFormatter from './formatters/NameAndIdFormatter';
import SearchDateRange from './formatters/SearchDateRange';
import {
  type ChangeLogFilters,
  CharterFilteringOptions,
  type LerePaginatedResponse,
  type LereTabComponentProps,
  type SurchargeSuggestionDB,
} from './types';

export type DateRange = {
  start: string;
  end: string;
};
export type FormInput = {
  propertyName: string;
  roomTypeName: string;
  actionedDateRange: DateRange;
  status: string;
  enforceRoomTypeGranularity: boolean;
  onlyCharters: CharterFilteringOptions;
};

export default function RMChangeLog({ onLereError }: LereTabComponentProps) {
  const [changeLogData, setChangeLogData] = useState<LerePaginatedResponse<SurchargeSuggestionDB>>({
    result: [],
    total: 0,
  });
  const [changeLogPaginationModel, setChangeLogPaginationModel] = useState<GridPaginationModel>({
    pageSize: 20,
    page: 0,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [displayDateRange, setDisplayDateRange] = useState<DateRange>({ start: '', end: '' });
  const [filterByDateRange, setFilterByDateRange] = useState<boolean>(false);
  const [filters, setFilters] = useState<undefined | ChangeLogFilters>({
    onlyCharters: CharterFilteringOptions.ONLY_NON_CHARTERS,
    enforceRoomTypeGranularity: false,
  });
  const { control, handleSubmit, reset, setValue } = useForm<FormInput>({
    defaultValues: {
      propertyName: '',
      roomTypeName: '',
      actionedDateRange: { start: dayjs().startOf('day').utc().format(), end: dayjs().endOf('day').utc().format() },
      status: 'all',
      onlyCharters: CharterFilteringOptions.ONLY_NON_CHARTERS,
      enforceRoomTypeGranularity: false,
    },
  });
  const exportCurrentPage = () => {
    const displayedRows = changeLogData.result.map((row) => ({
      offerName: row.offer_name,
      offerId: row.offer_id,
      propertyName: row.property_name,
      propertyId: row.property_id,
      roomTypeName: row.room_type_name,
      roomTypeId: row.room_type_id,
      roomRateId: row.room_rate_id,
      suggestedStartDate: row.suggested_start_date,
      suggestedEndDate: row.suggested_end_date,
      actionedStartDate: row.actioned_start_date,
      actionedEndDate: row.actioned_end_date,
      beforeNightlyPrice: row.before_price,
      afterNightlyPrice: row.suggested_after_price,
      suggestedSurcharge: row.suggested_surcharge,
      actionedSurcharge: row.exp_actioned_surcharge ?? row.actioned_surcharge,
      actionedByUsername: row.created_by_username,
      actionedByUserId: row.created_by_user_id,
      confidenceScore: row.confidence,
      status: row.status,
      actionedAt: row.created_at,
      booked: row.booked,
      allocated: row.allocated,
      note: row.note,
      url: `https://admin.luxuryescapes.com/vendors/${row.vendor_id}/properties/${row.property_id}/room-types/${row.room_type_id}/room-rates/${row.room_rate_id}`,
    }));
    const csvString = unparse(displayedRows);
    const currentDate = formatDateNumeralMonths(new Date());
    const currentPage = changeLogPaginationModel.page;
    const filename = `change-log-export-page-${currentPage + 1}-date-${currentDate}.csv`;
    fileDownload(csvString, filename);
  };
  const handleReset = () => {
    setDisplayDateRange({ start: formatDateSlashes(new Date()), end: formatDateSlashes(new Date()) });
    reset();
  };
  const onSubmit: SubmitHandler<FormInput> = (data) => {
    const newFilters = {
      propertyName: data.propertyName,
      roomTypeName: data.roomTypeName,
      actionedDateRange: filterByDateRange ? data.actionedDateRange : undefined,
      status: data.status === 'all' ? undefined : data.status,
      onlyCharters: data.onlyCharters,
      enforceRoomTypeGranularity: data.enforceRoomTypeGranularity,
    };

    if (isEqual(newFilters, filters)) {
      return;
    }
    setFilters(newFilters);
  };

  const columns: Array<GridColDef<SurchargeSuggestionDB>> = [
    {
      field: 'offer_id',
      headerName: 'Offer',
      display: 'flex',
      flex: 1,
      renderCell: ({ row }) => (
        <NameAndIdFormatter name={row.offer_name} id={row.offer_id} to={`/offers/${row.offer_id}`} />
      ),
    },
    {
      field: 'property_id',
      headerName: 'Property',
      display: 'flex',
      flex: 1,
      renderCell: ({ row }) => (
        <NameAndIdFormatter
          name={row.property_name}
          id={row.property_id}
          to={`/vendors/${row.vendor_id}/properties/${row.property_id}`}
        />
      ),
    },
    {
      field: 'room_type_id',
      headerName: 'Room Type',
      display: 'flex',
      flex: 1,
      renderCell: ({ row }) => (
        <NameAndIdFormatter
          name={row.room_type_name}
          id={row.room_type_id}
          to={`/vendors/${row.vendor_id}/properties/${row.property_id}/room-types/${row.room_type_id}/room-rates/${row.room_rate_id}`}
        />
      ),
    },
    {
      field: 'suggested',
      headerName: 'Suggested',
      display: 'flex',
      flex: 2,
      renderCell: ({ row }) => (
        <Box>
          <Typography variant="body1">
            <Typography fontWeight="bold" display="inline" component="span">
              Date Range:
            </Typography>{' '}
            {`${formatDateSlashes(row.suggested_start_date)} - ${formatDateSlashes(row.suggested_end_date)}`}
          </Typography>
          <Typography variant="body1">
            <Typography fontWeight="bold" display="inline" component="span">
              Before Nightly Price:
            </Typography>{' '}
            {currencyFormatter('AUD', row.before_price, 2)}
          </Typography>
          <Typography variant="body1">
            <Typography fontWeight="bold" display="inline" component="span">
              After Nightly Price:
            </Typography>{' '}
            {currencyFormatter('AUD', row.suggested_after_price, 2)}
          </Typography>
          <Typography variant="body1">
            <Typography fontWeight="bold" display="inline" component="span">
              Suggested Rev Surcharge:
            </Typography>{' '}
            {currencyFormatter('AUD', row.suggested_surcharge, 2)}
          </Typography>
          {row.confidence && <ConfidenceScoreFormatter confidence={row.confidence} />}
        </Box>
      ),
    },
    {
      field: 'actioned',
      headerName: 'Actioned',
      display: 'flex',
      flex: 2,
      renderCell: ({ row }) => (
        <Box>
          {row.exp_actioned_surcharge != undefined && (
            <Chip component="span" label="Exploration Group" size="small" color="success" />
          )}
          <Typography variant="body1">
            <Typography fontWeight="bold" display="inline" component="span">
              Date Range:
            </Typography>{' '}
            {`${formatDateSlashes(row.actioned_start_date)} - ${formatDateSlashes(row.actioned_end_date)}`}
          </Typography>
          <Typography variant="body1">
            <Typography fontWeight="bold" display="inline" component="span">
              Rev Surcharge:
            </Typography>{' '}
            {currencyFormatter('AUD', row.exp_actioned_surcharge ?? row.actioned_surcharge, 2)}
          </Typography>
          {row.created_by_user_id && row.created_by_username && (
            <Typography variant="body1">
              <Typography fontWeight="bold" display="inline" component="span">
                Actioned By:
              </Typography>{' '}
              <Link component={RouterLink} to={`/users/${row.created_by_user_id}`} target="_blank" title="id">
                {row.created_by_username}
              </Link>
            </Typography>
          )}
          {row?.note && (
            <Typography variant="body1">
              <Typography fontWeight="bold" display="inline" component="span">
                Note:
              </Typography>{' '}
              {row.note}
            </Typography>
          )}
          <Typography variant="body1">
            <Typography fontWeight="bold" display="inline" component="span">
              Room Rate Id:
            </Typography>{' '}
            {row.room_rate_id}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'inventory',
      headerName: 'Inventory',
      display: 'flex',
      flex: 2,
      renderCell: ({ row }) => (
        <Box>
          {
            //either all of these fields will be populated or none of them, so no need to check each one
            row.booked !== null && row.booked !== undefined && (
              <Typography variant="body1">
                <Typography fontWeight="bold" display="inline" component="span">
                  Booked / Allocated:
                </Typography>{' '}
                {row.booked + '/' + row.allocated}
              </Typography>
            )
          }
          {
            //either all of these fields will be populated or none of them, so no need to check each one
            row.allocated_sum_minus_two_days !== null && row.allocated_sum_minus_two_days !== undefined && (
              <Typography variant="body1">
                <Typography fontWeight="bold" display="inline" component="span">
                  Booked / Allocated (+-2 days):
                </Typography>{' '}
                {`${row.booked_sum_minus_two_days + row.booked_sum_plus_two_days}/${
                  row.allocated_sum_minus_two_days + row.allocated_sum_plus_two_days
                }`}
              </Typography>
            )
          }
        </Box>
      ),
    },
    {
      field: 'details',
      headerName: 'Details',
      display: 'flex',
      flex: 2,
      renderCell: ({ row }) => (
        <Box>
          <Typography variant="body1">
            <Typography fontWeight="bold" display="inline" component="span">
              Status:
            </Typography>{' '}
            {row.status.toUpperCase()}
          </Typography>
          <Typography variant="body1">
            <Typography fontWeight="bold" display="inline" component="span">
              Actioned At:
            </Typography>{' '}
            {formatDateSlashesWithClock(dateNowUtc(row.created_at))}
          </Typography>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    setIsLoading(true);
    LereService.getChangeLog(changeLogPaginationModel.page + 1, changeLogPaginationModel.pageSize, filters)
      .then(setChangeLogData)
      .catch(onLereError)
      .finally(() => setIsLoading(false));
  }, [changeLogPaginationModel, setChangeLogData, onLereError, filters]);
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name: 'actionedDateRange',
  });
  return (
    <Box>
      <Typography variant="h2" fontSize="2em" fontWeight="medium">
        Search Change Log
      </Typography>
      <Typography variant="body1">Surcharge suggestions that have been interacted with in the past.</Typography>
      <Stack direction="column" my={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Input disabled={isLoading} fullWidth control={control} muiLabel="Property Name" name="propertyName" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input disabled={isLoading} fullWidth control={control} muiLabel="Room Type Name" name="roomTypeName" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Select disabled={isLoading} control={control} muiLabel="Status" name="status">
              <MenuItem key="all" value="all">
                ALL
              </MenuItem>
              <MenuItem key="accepted" value="accepted">
                ACCEPTED
              </MenuItem>
              <MenuItem key="rejected" value="rejected">
                REJECTED
              </MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <SearchDateRange
              displayDateRange={displayDateRange}
              setDisplayDateRange={setDisplayDateRange}
              dateRangeFormKey="actionedDateRange"
              setDateRangeValue={setValue}
              disabled={!filterByDateRange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <CharterTypeFiltersDropdown control={control} name="onlyCharters" disabled={isLoading} />
          </Grid>
        </Grid>
        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={filterByDateRange}
                onChange={(e) => setFilterByDateRange(e.target.checked)}
                disabled={isLoading}
              />
            }
            label="Filter by Date Range"
          />
          <FormCheckbox
            disabled={isLoading}
            control={control}
            label="Room Type Granularity"
            name="enforceRoomTypeGranularity"
          />
        </Box>
        <Stack direction="row" justifyContent="end" spacing={2}>
          <Button variant="text" disabled={isLoading} onClick={exportCurrentPage}>
            Export Current Page as CSV
          </Button>
          <Button variant="text" disabled={isLoading} onClick={handleReset}>
            Clear all filters
          </Button>
          <Button variant="contained" type="submit" disabled={isLoading} onClick={handleSubmit(onSubmit)}>
            Search
          </Button>
        </Stack>
      </Stack>
      <DataGrid
        autoHeight
        columns={columns}
        rows={changeLogData.result}
        rowCount={changeLogData.total}
        loading={isLoading}
        pagination
        paginationMode="server"
        paginationModel={changeLogPaginationModel}
        pageSizeOptions={[5, 10, 20, 50, 100, 200, 500]}
        onPaginationModelChange={(model) => setChangeLogPaginationModel(model)}
        getRowHeight={() => 'auto'}
        slots={{
          pagination: GridPagination,
        }}
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
        disableColumnSorting
        disableMultipleRowSelection
      />
    </Box>
  );
}
